// frontend/src/components/dashboard/ai/AiDialog.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Paper,
  Divider,
  Chip,
  LinearProgress,
  IconButton,
  useTheme
} from '@mui/material';
import { 
  Save, 
  Close, 
  Bookmark,
  BookmarkBorder,
  LightbulbOutlined,
  CheckCircleOutlined
} from '@mui/icons-material';

const AiDialog = ({ 
  open, 
  onClose, 
  currentAiResponse,
  isLoading,
  handleSaveInsight,
  priority = 'medium'
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDarkMode = theme.palette.mode === 'dark';

  const priorityColors = {
    low: theme.palette.info.main,
    medium: theme.palette.warning.main,
    high: theme.palette.error.main
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          overflow: 'hidden',
          // Use theme-aware background
          background: isDarkMode 
            ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)'
            : 'linear-gradient(135deg, #fff 0%, #f5f7ff 100%)',
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        // Keep the gradient but make it theme-aware
        background: isDarkMode
          ? 'linear-gradient(135deg, #4a5568 0%, #553c8b 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        py: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LightbulbOutlined sx={{ mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {t('ai.dialog.title')}
          </Typography>
        </Box>
        <Box>
          <Chip 
            label={t(`ai.priority${priority.charAt(0).toUpperCase() + priority.slice(1)}`)}
            size="small"
            sx={{ 
              backgroundColor: `${priorityColors[priority]}20`, 
              color: priorityColors[priority],
              mr: 1,
              fontWeight: 500
            }}
          />
          <IconButton 
            onClick={onClose}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading ? (
          <Box sx={{ p: 4 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2, 
                textAlign: 'center',
                color: theme.palette.text.primary // Theme-aware text color
              }}
            >
              {t('ai.dialog.analyzing')}
            </Typography>
            <LinearProgress 
              sx={{ 
                borderRadius: 1,
                height: 8,
                mb: 2,
                backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
                "& .MuiLinearProgress-bar": {
                  background: isDarkMode
                    ? 'linear-gradient(90deg, #4a5568 0%, #553c8b 100%)'
                    : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                }
              }} 
            />
            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center',
                color: theme.palette.text.secondary // Theme-aware secondary text
              }}
            >
              {t('ai.dialog.preparingInsights')}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Paper elevation={0} sx={{ 
              m: 3, 
              p: 3, 
              borderRadius: 2,
              // Theme-aware border and background
              border: isDarkMode 
                ? '1px solid rgba(255, 255, 255, 0.15)' 
                : '1px solid rgba(102, 126, 234, 0.15)',
              background: isDarkMode 
                ? 'rgba(42, 42, 42, 0.8)' 
                : 'rgba(255, 255, 255, 0.8)',
            }}>
              <Box 
                dangerouslySetInnerHTML={{ __html: currentAiResponse }}
                sx={{
                  // Theme-aware content styling
                  color: theme.palette.text.primary,
                  '& h2, & h3, & h4': {
                    fontWeight: 600,
                    mb: 2,
                    color: theme.palette.text.primary, // Use theme text color
                    '&:not(:first-of-type)': {
                      mt: 3
                    }
                  },
                  '& h2': { fontSize: '1.5rem' },
                  '& h3': { fontSize: '1.25rem' },
                  '& h4': { fontSize: '1.1rem' },
                  '& p': {
                    mb: 2,
                    lineHeight: 1.6,
                    color: theme.palette.text.primary // Theme-aware paragraph color
                  },
                  '& ul, & ol': {
                    pl: 3,
                    mb: 2,
                    color: theme.palette.text.primary // Theme-aware list color
                  },
                  '& li': {
                    mb: 1,
                    color: theme.palette.text.primary // Theme-aware list item color
                  },
                  '& strong': {
                    fontWeight: 600,
                    color: theme.palette.primary.main
                  },
                  // Additional dark mode text fixes
                  '& *': {
                    color: 'inherit !important' // Force inherit color for all elements
                  }
                }}
              />
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        justifyContent: 'space-between',
        backgroundColor: theme.palette.background.paper // Theme-aware background
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          startIcon={<Close />}
        >
          {t('ai.dialog.close')}
        </Button>
        
        {!isLoading && currentAiResponse && (
          <Button 
            onClick={handleSaveInsight}
            variant="contained"
            startIcon={<Save />}
            sx={{ 
              background: isDarkMode
                ? 'linear-gradient(135deg, #4a5568 0%, #553c8b 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: isDarkMode
                  ? 'linear-gradient(135deg, #3a4454 0%, #453177 100%)'
                  : 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
              }
            }}
          >
            {t('ai.dialog.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AiDialog;